import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import apiService, { BaseURL } from "../../../../../../service/Api";
import type { RootState } from "../../../../../../store/Index";
import type { DeceasedPerson } from "../../../../../bookings/types/edit-booking";
import { useGetFooterConfigQuery } from "../../../../../../service/footerConfigApi";
import { useMemorialTheme } from "./useTheme";
import type { ICommentType } from "../tabs/MemoriesTab";
import type {
  IStreamDetails,
  IStreamResponse,
} from "../types/memorial-types";
import type { CalendarEvent } from "../types/AddtoCalendarutils";
import streamLinks from "../../old-stream-links.json";

interface IMessage {
  id: number;
  sender_full_name: string | null;
  sender_email: string;
  sender_phone_number: string | null;
  content: string;
  created_at: string;
  is_approved: boolean;
  parent: number | null;
}

interface IApiMessageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IMessage[];
}

type EnrichedStream = {
  index: number;
  start: Date | null;
  end: Date | null;
};

type ValidStream = {
  index: number;
  start: Date;
  end: Date;
};

interface StreamProgramResponse {
  id: number;
  file?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const isValidStream = (s: EnrichedStream): s is ValidStream =>
  s.start instanceof Date && s.end instanceof Date;

const URL_TO_BOOKING_ID: Readonly<Record<string, string>> = Object.freeze(
  streamLinks.reduce(
    (acc, { booking_id, website_stream_link }) => {
      acc[website_stream_link] = String(booking_id);
      return acc;
    },
    {} as Record<string, string>,
  ),
);

export function useMemorialPageState() {
  const [activeStreamIndex, setActiveStreamIndex] = useState(0);
  const [donationRefreshKey, setDonationRefreshKey] = useState(0);
  const [deceasedData, setDeceasedData] = useState<DeceasedPerson | null>(
    null,
  );
  const [streams, setStreams] = useState<IStreamDetails[]>([]);
  const [messages, setMessages] = useState<ICommentType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const pageSize = 10;
  const [isLoadingDeceased, setIsLoadingDeceased] = useState(false);
  const [isLoadingStreams, setIsLoadingStreams] = useState(false);
  const [streamProgram, setStreamProgram] = useState<string | null>(null);
  const { id: bookingIdParam } = useParams<{
    id: string;
    pageType: string;
  }>();
  const [bookingId, setBookingId] = useState<string | undefined>(undefined);
  const socketRef = useRef<WebSocket | null>(null);
  const [downloadingProgram, setDownloadingProgram] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const isAdmin =
    auth.user?.permissions.includes("chat.delete_memorialmessage") || false;
  const isStaging = BaseURL.includes("staging.muzukuru.com");

  const memorialTheme = useMemorialTheme(streams);
  const { data: footerConfig } = useGetFooterConfigQuery("MMS");

  const footerContactPhone = footerConfig?.contact_details?.find(
    (c) => c.type === "phone",
  )?.value;
  const footerContactEmail = footerConfig?.contact_details?.find(
    (c) => c.type === "email",
  )?.value;
  const footerFacebookUrl = footerConfig?.socials?.find(
    (s) => s.platform === "Facebook",
  )?.url;
  const footerLinkedinUrl = footerConfig?.socials?.find(
    (s) => s.platform === "LinkedIn",
  )?.url;
  const footerYoutubeUrl = footerConfig?.socials?.find(
    (s) => s.platform === "YouTube",
  )?.url;
  const footerWhatsappUrl = footerContactPhone
    ? `https://wa.me/${footerContactPhone.replace(/\D/g, "")}`
    : undefined;
  const currentStream = streams[activeStreamIndex];

  const fetchStreamDetails = useCallback(async () => {
    if (!bookingId) return;
    setIsLoadingStreams(true);
    try {
      const response = await apiService.get<IStreamResponse>(
        `/api/streams/memorial-stream/?booking=${bookingId}`,
      );

      setStreams(response.data.results);

      if (response.data.results.length === 1) {
        setActiveStreamIndex(0);
      }
    } catch (error) {
      console.error("Failed to fetch stream details:", error);
      showErrorToast("Failed to load stream details.", "stream-details-error");
      setStreams([]);
    } finally {
      setIsLoadingStreams(false);
    }
  }, [bookingId]);

  const fetchDeceasedDetails = useCallback(async (deceasedId: number) => {
    setIsLoadingDeceased(true);
    try {
      const response = await apiService.get<DeceasedPerson>(
        `/api/individuals/deceased/${deceasedId}/retrieve-by-id/`,
        {},
        true,
      );

      setDeceasedData(response.data);
    } catch (error) {
      console.error("Failed to fetch deceased individual details:", error);
      showErrorToast(
        "Failed to load obituary details.",
        "obituary-details-error",
      );
      setDeceasedData(null);
    } finally {
      setIsLoadingDeceased(false);
    }
  }, []);

  useEffect(() => {
    fetchStreamDetails();
  }, [fetchStreamDetails]);

  useEffect(() => {
    if (currentStream?.booking?.deceased?.[0]?.id) {
      fetchDeceasedDetails(currentStream.booking.deceased[0].id);
    }
  }, [currentStream, fetchDeceasedDetails]);

  useEffect(() => {
    const currentUrl = window.location.href;

    const mappedId = URL_TO_BOOKING_ID[currentUrl];

    if (mappedId) {
      setBookingId(mappedId);
    } else {
      setBookingId(bookingIdParam);
    }
  }, [bookingIdParam]);

  const handleStreamChange = (streamIndex: number) => {
    setActiveStreamIndex(streamIndex);
  };

  const buildMessageTree = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const map: { [key: string]: ICommentType } = {};
      const roots: ICommentType[] = [];

      msgs.forEach((msg) => {
        map[msg.id] = { ...msg, replies: [] };
      });

      msgs.forEach((msg) => {
        if (msg.parent_message && map[msg.parent_message]) {
          map[msg.parent_message].replies!.push(map[msg.id]);
        } else {
          roots.push(map[msg.id]);
        }
      });

      const sortMessages = (messages: ICommentType[]) => {
        messages.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        messages.forEach((m) => {
          if (m.replies) sortMessages(m.replies);
        });
      };

      sortMessages(roots);
      return roots;
    },
    [],
  );

  const getServiceStart = useCallback(
    (stream: { date?: string | null; start?: string | null }): Date | null => {
      const date = stream.date;
      const start = stream.start;

      if (!date || !start) {
        console.warn("Missing start fields", stream);
        return null;
      }

      const d = new Date(`${date}T${start}+02:00`);

      if (isNaN(d.getTime())) {
        console.warn("Invalid START date", { date, start });
        return null;
      }

      return d;
    },
    [],
  );

  const getServiceEnd = useCallback(
    (stream: { date?: string | null; end?: string | null }): Date | null => {
      const date = stream.date;
      const end = stream.end;

      if (!date || !end) {
        console.warn("Missing end fields", stream);
        return null;
      }

      const d = new Date(`${date}T${end}+02:00`);

      if (isNaN(d.getTime())) {
        console.warn("Invalid END date", { date, end });
        return null;
      }

      return d;
    },
    [],
  );

  const getDefaultActiveStreamIndex = useCallback(
    (
      streams: Array<{
        date?: string | null;
        start?: string | null;
        end?: string | null;
      }>,
    ) => {
      if (!streams.length) return 0;

      const now = new Date();

      const enriched: EnrichedStream[] = streams.map((stream, index) => ({
        index,
        start: getServiceStart(stream),
        end: getServiceEnd(stream),
      }));

      const valid = enriched.filter(isValidStream);

      if (!valid.length) return 0;

      const ongoing = valid.find((s) => now >= s.start && now <= s.end);

      if (ongoing) {
        return ongoing.index;
      }

      const upcoming = valid
        .filter((s) => s.start > now)
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      if (upcoming.length) {
        return upcoming[0].index;
      }

      const past = valid
        .filter((s) => s.end < now)
        .sort((a, b) => b.end.getTime() - a.end.getTime());

      return past[0]?.index ?? 0;
    },
    [getServiceEnd, getServiceStart],
  );

  useEffect(() => {
    if (streams.length) {
      const defaultIndex = getDefaultActiveStreamIndex(streams);
      setActiveStreamIndex(defaultIndex);
    }
  }, [getDefaultActiveStreamIndex, streams]);

  const flattenMessages = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const flatten = (messages: ICommentType[]): ICommentType[] => {
        let flat: ICommentType[] = [];
        messages.forEach((m) => {
          flat.push({ ...m, replies: undefined });
          if (m.replies) {
            flat = flat.concat(flatten(m.replies));
          }
        });
        return flat;
      };
      return flatten(msgs);
    },
    [],
  );

  const fetchMessages = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      let query = `stream_id=${currentStream.id}&page=${currentPage}`;

      if (!isAdmin) {
        query += "&is_approved=true";
      }

      const response = await apiService.get<IApiMessageResponse>(
        `/api/chats/memorial-message/?${query}`,
        undefined,
        true,
      );

      const fetched = response.data.results.map((msg: IMessage) => ({
        id: msg.id.toString(),
        author: msg.sender_full_name || "Anonymous",
        date: new Date(msg.created_at).toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        content: msg.content,
        is_approved: msg.is_approved,
        parent_message: msg.parent ? msg.parent.toString() : undefined,
      }));

      setMessages(buildMessageTree(fetched));
      setTotalCount(response.data.count);
      setHasNextPage(Boolean(response.data.next));
      setHasPreviousPage(Boolean(response.data.previous));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      showErrorToast("Failed to load messages.", "messages-fetch-error");
    }
  }, [buildMessageTree, currentStream?.id, isAdmin, currentPage]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const removeById = useCallback(
    (msgs: ICommentType[], id: string): ICommentType[] => {
      return msgs
        .filter((m) => m.id !== id)
        .map((m) => ({
          ...m,
          replies: m.replies ? removeById(m.replies, id) : [],
        }));
    },
    [],
  );

  useEffect(() => {
    if (currentStream?.id) {
      const wsProtocol = BaseURL.startsWith("https") ? "wss" : "ws";
      const wsHost = new URL(BaseURL).host;
      const wsUrl = `${wsProtocol}://${wsHost}/ws/stream-memorial/${currentStream.id}/`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("Connected to WebSocket");
        ws.send(JSON.stringify({ type: "refetch" }));
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.error) {
            showErrorToast(data.error, "websocket-error");
            return;
          }
          if (data.type === "delete") {
            setMessages((prev) => removeById(prev, data.id));
          } else if (data.type === "all_messages") {
            const fetchedMessages = (
              data.messages as Array<{
                id: number | string;
                sender_full_name?: string | null;
                timestamp?: string;
                message?: string;
                is_approved?: boolean;
                parent_message?: number | string | null;
              }>
            ).map((msg) => ({
              id: msg.id.toString(),
              author: msg.sender_full_name || "Anonymous",
              date: new Date(msg.timestamp || Date.now()).toLocaleString(
                "en-US",
                {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              ),
              content: msg.message || "",
              is_approved: msg.is_approved,
              parent_message: msg.parent_message
                ? msg.parent_message.toString()
                : undefined,
            }));
            setMessages(buildMessageTree(fetchedMessages));
          } else {
            const newMsg: ICommentType = {
              id: data.id.toString(),
              author: data.sender_full_name || "Anonymous",
              date: new Date(data.timestamp || Date.now()).toLocaleString(
                "en-US",
                {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              ),
              content: data.message || "",
              is_approved: data.is_approved,
              parent_message: data.parent_message
                ? data.parent_message.toString()
                : undefined,
            };

            setMessages((prev) => {
              const flatPrev = flattenMessages(prev);
              const existsIndex = flatPrev.findIndex((m) => m.id === newMsg.id);
              let updatedFlat;
              if (existsIndex !== -1) {
                updatedFlat = [...flatPrev];
                updatedFlat[existsIndex] = newMsg;
              } else {
                updatedFlat = [...flatPrev, newMsg];
              }
              return buildMessageTree(updatedFlat);
            });
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e, event.data);
        }
      };
      ws.onerror = (err) => console.error("WebSocket error:", err);
      ws.onclose = () => console.log("WebSocket disconnected");

      socketRef.current = ws;

      return () => {
        ws.close();
      };
    }
  }, [
    buildMessageTree,
    currentStream?.id,
    flattenMessages,
    isAdmin,
    removeById,
  ]);

  const handleApprove = (id: string, approve: boolean) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can approve messages.",
        "approve-permission-error",
      );
      return;
    }
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "approve", id, approve }));
      showSuccessToast(
        `Message ${approve ? "approved" : "disapproved"} successfully!`,
      );
      setTimeout(() => fetchMessages(), 1500);
    } else {
      showErrorToast("Failed to update approval.", "approve-error");
    }
  };

  const handleDelete = (id: string) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can delete messages.",
        "delete-permission-error",
      );
      return;
    }
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "delete", id }));
      showSuccessToast("Message deleted successfully!");
      setTimeout(() => fetchMessages(), 1500);
    } else {
      showErrorToast("Failed to delete message.", "delete-error");
    }
  };

  const onMessageSent = useCallback(() => {
    setTimeout(() => fetchMessages(), 1500);
  }, [fetchMessages]);

  const conferenceCalendarEvent: CalendarEvent | null = (() => {
    const { title, date, start, end } = currentStream ?? {};
    if (!date || !start || !end) return null;

    const startDate = new Date(`${date}T${start}+02:00`);
    const endDate = new Date(`${date}T${end}+02:00`);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    const venueName =
      currentStream?.service?.venue?.name ||
      currentStream?.event_details?.venue?.name ||
      currentStream?.booking?.events?.[0]?.venue?.name ||
      "";

    const location = [venueName, currentStream?.city]
      .filter((v) => v && v !== "N/A")
      .join(", ");

    return {
      title: title || "Conference Event",
      location: location || undefined,
      startDate,
      endDate,
    };
  })();

  const fetchProgram = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      const res = await apiService.get<
        PaginatedResponse<StreamProgramResponse>
      >(`/api/streams/stream-program/?stream=${currentStream?.id}`);

      const program = res.data.results?.[0];

      setStreamProgram(program?.file ?? null);
    } catch (err) {
      console.error("Failed to fetch program", err);
    }
  }, [currentStream?.id]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleDownloadProgram = async () => {
    if (!streamProgram) {
      showErrorToast("No program available to download");
      return;
    }

    try {
      setDownloadingProgram(true);

      const response = await fetch(streamProgram, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers.get("Content-Disposition");

      let filename = "stream-program";

      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
      } else {
        filename = decodeURIComponent(
          streamProgram.split("/").pop() || "stream-program",
        );
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      showErrorToast("Failed to download program");
    } finally {
      setDownloadingProgram(false);
    }
  };

  const onDonationSuccess = useCallback(() => {
    setDonationRefreshKey((k) => k + 1);
  }, []);

  return {
    streams,
    activeStreamIndex,
    currentStream,
    handleStreamChange,
    isLoadingStreams,
    isLoadingDeceased,
    deceasedData,
    memorialTheme,
    isAdmin,
    isStaging,
    socketRef,
    messages,
    handleApprove,
    handleDelete,
    onMessageSent,
    fetchMessages,
    currentPage,
    totalCount,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    onNextPage: handleNextPage,
    onPreviousPage: handlePreviousPage,
    streamProgram,
    downloadingProgram,
    handleDownloadProgram,
    donationRefreshKey,
    onDonationSuccess,
    fetchStreamDetails,
    conferenceCalendarEvent,
    footerContactPhone,
    footerContactEmail,
    footerFacebookUrl,
    footerLinkedinUrl,
    footerYoutubeUrl,
    footerWhatsappUrl,
  };
}
